import React from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Paper, Typography, Divider, Stack, Box, Button, IconButton, Tooltip } from '@mui/material';
import { useSetAtom } from 'jotai';

import { PermissionGate } from '@/core/auth/permissions';
import { MLPersona } from '@/shared/components/MLPersona/MLPersona';
import { openCommentModalAtom } from '@/shared/state/atoms';

interface CommentsPaneProps {
  record: {
    comments: {
      id: string;
      avatarUrl?: string;
      text: string;
      createdDate: string;
      updatedDate: string;
      updatedBy: string;
    }[];
  };
}

const CommentsPane: React.FC<CommentsPaneProps> = ({ record }) => {
  const openModal = useSetAtom(openCommentModalAtom);

  const handleAddComment = () => {
    openModal({ mode: 'add', data: null });
  };

  const handleEditComment = (comment: CommentsPaneProps['record']['comments'][number]) => {
    openModal({ mode: 'edit', data: comment });
  };

  const handleDeleteComment = (comment: CommentsPaneProps['record']['comments'][number]) => {
    openModal({ mode: 'delete', data: comment });
  };
  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Comments</Typography>
        <PermissionGate resource="EngagementRecords" action="create">
          <Button variant="outlined" size="small" onClick={handleAddComment}>
            Add Comment
          </Button>
        </PermissionGate>
      </Box>
      <Divider sx={{ mb: 2 }} />
      {record.comments.length > 0 ? (
        <Stack spacing={2} divider={<Divider />}>
          {[...record.comments]
            .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
            .map((comment) => (
              <Box key={comment.id} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <MLPersona
                  user={{
                    DisplayName: comment.updatedBy,
                    ImageUrlSmall: comment.avatarUrl ?? undefined,
                    ImageUrlMedium: comment.avatarUrl ?? undefined,
                    ImageUrlLarge: comment.avatarUrl ?? undefined,
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
                    <Box>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                        <Typography variant="subtitle2">{comment.updatedBy}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(comment.updatedDate).toLocaleString()}
                        </Typography>
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={0.5}>
                      <PermissionGate resource="EngagementRecords" action="update">
                        <Tooltip title="Edit comment">
                          <IconButton size="small" onClick={() => handleEditComment(comment)} aria-label="Edit comment">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </PermissionGate>
                      <PermissionGate resource="EngagementRecords" action="delete">
                        <Tooltip title="Delete comment">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteComment(comment)}
                            aria-label="Delete comment"
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </PermissionGate>
                    </Stack>
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {comment.text}
                  </Typography>
                </Box>
              </Box>
            ))}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No comments
        </Typography>
      )}
    </Paper>
  );
};

export default CommentsPane;
